import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { BadgePill } from '@/components/campus';
import {
  Building2,
  GraduationCap,
  Calendar,
  QrCode,
  CreditCard,
  TrendingUp,
  Users,
  Loader2,
  RefreshCw,
  AlertTriangle,
  DollarSign,
  Ticket,
  Megaphone,
  Building,
  Clock,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Format helpers
const formatNumber = (num) => {
  if (!num && num !== 0) return '—';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// ==================== STAT STRIP ====================
const StatStrip = ({ stats, loading }) => {
  const items = [
    { label: 'Campuses', value: stats?.total_campuses, icon: GraduationCap, color: 'text-electric-ice-blue' },
    { label: 'Organizations', value: stats?.total_organizations, icon: Building2, color: 'text-cool-mint' },
    { label: 'Active Events', value: stats?.active_events_today, icon: Calendar, color: 'text-soft-amber' },
    { label: 'Total Check-ins', value: stats?.total_checkins, icon: QrCode, color: 'text-electric-ice-blue' },
    { label: 'Subscriptions', value: stats?.active_subscriptions, icon: CreditCard, color: 'text-cool-mint' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="stat-strip">
      {items.map((item, index) => {
        const Icon = item.icon;
        return (
          <div
            key={index}
            className="p-4 bg-graphite/50 rounded-xl border border-white/[0.06] hover:border-white/[0.12] transition-colors"
          >
            <div className="flex items-center gap-3 mb-2">
              <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center", 
                item.color === 'text-electric-ice-blue' ? 'bg-electric-ice-blue/10' :
                item.color === 'text-cool-mint' ? 'bg-cool-mint/10' : 'bg-soft-amber/10'
              )}>
                <Icon className={cn("w-4 h-4", item.color)} />
              </div>
              <span className="text-xs text-muted-text uppercase tracking-wider">{item.label}</span>
            </div>
            <p className="text-2xl font-bold text-cloud-white font-mono">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : formatNumber(item.value)}
            </p>
          </div>
        );
      })}
    </div>
  );
};

// ==================== TOP CAMPUSES TABLE ====================
const TopCampusesTable = ({ campuses, loading }) => {
  return (
    <div className="bg-graphite/50 rounded-xl border border-white/[0.06] overflow-hidden" data-testid="top-campuses">
      <div className="p-4 border-b border-white/[0.06]">
        <h3 className="font-semibold text-cloud-white flex items-center gap-2">
          <GraduationCap className="w-5 h-5 text-electric-ice-blue" />
          Top Campuses
        </h3>
      </div>
      
      {loading ? (
        <div className="p-8 text-center">
          <Loader2 className="w-6 h-6 text-electric-ice-blue animate-spin mx-auto" />
        </div>
      ) : campuses.length === 0 ? (
        <div className="p-8 text-center text-muted-text">No campus data yet</div>
      ) : (
        <div className="divide-y divide-white/[0.06]">
          {campuses.map((campus, index) => (
            <div key={campus.id || index} className="flex items-center justify-between p-4 hover:bg-white/[0.02]">
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-electric-ice-blue/10 flex items-center justify-center text-xs font-bold text-electric-ice-blue">
                  {index + 1}
                </span>
                <span className="font-medium text-cloud-white">{campus.name}</span>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div className="text-center">
                  <p className="font-mono text-cloud-white">{formatNumber(campus.organization_count)}</p>
                  <p className="text-xs text-muted-text">Orgs</p>
                </div>
                <div className="text-center">
                  <p className="font-mono text-cloud-white">{formatNumber(campus.event_count)}</p>
                  <p className="text-xs text-muted-text">Events</p>
                </div>
                <div className="text-center">
                  <p className="font-mono text-cool-mint">{formatNumber(campus.checkin_count)}</p>
                  <p className="text-xs text-muted-text">Check-ins</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== TOP ORGANIZATIONS TABLE ====================
const TopOrganizationsTable = ({ organizations, loading }) => {
  return (
    <div className="bg-graphite/50 rounded-xl border border-white/[0.06] overflow-hidden" data-testid="top-organizations">
      <div className="p-4 border-b border-white/[0.06]">
        <h3 className="font-semibold text-cloud-white flex items-center gap-2">
          <Building2 className="w-5 h-5 text-cool-mint" />
          Top Organizations
        </h3>
      </div>
      
      {loading ? (
        <div className="p-8 text-center">
          <Loader2 className="w-6 h-6 text-cool-mint animate-spin mx-auto" />
        </div>
      ) : organizations.length === 0 ? (
        <div className="p-8 text-center text-muted-text">No organizations yet</div>
      ) : (
        <div className="divide-y divide-white/[0.06]">
          {organizations.map((org, index) => (
            <div key={org.id || index} className="flex items-center justify-between p-4 hover:bg-white/[0.02]">
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-cool-mint/10 flex items-center justify-center text-xs font-bold text-cool-mint">
                  {index + 1}
                </span>
                <div>
                  <p className="font-medium text-cloud-white">{org.name}</p>
                  <p className="text-xs text-muted-text">{org.campus_name || 'No campus'}</p>
                </div>
              </div>
              <div className="flex items-center gap-6 text-sm">
                <div className="text-center">
                  <p className="font-mono text-cloud-white">{formatNumber(org.event_count)}</p>
                  <p className="text-xs text-muted-text">Events</p>
                </div>
                <div className="text-center">
                  <p className="font-mono text-cool-mint">{formatNumber(org.checkin_count)}</p>
                  <p className="text-xs text-muted-text">Check-ins</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== ACTIVITY FEED ====================
const ActivityFeed = ({ activity, loading }) => {
  const getIcon = (type) => {
    switch (type) {
      case 'organization_created': return Building;
      case 'event_published': return Calendar;
      case 'high_checkins': return TrendingUp;
      default: return Clock;
    }
  };

  const getColor = (type) => {
    switch (type) {
      case 'organization_created': return 'text-cool-mint bg-cool-mint/10';
      case 'event_published': return 'text-electric-ice-blue bg-electric-ice-blue/10';
      case 'high_checkins': return 'text-soft-amber bg-soft-amber/10';
      default: return 'text-muted-text bg-white/[0.05]';
    }
  };

  return (
    <div className="bg-graphite/50 rounded-xl border border-white/[0.06] overflow-hidden" data-testid="activity-feed">
      <div className="p-4 border-b border-white/[0.06]">
        <h3 className="font-semibold text-cloud-white flex items-center gap-2">
          <Clock className="w-5 h-5 text-soft-amber" />
          Recent Activity
        </h3>
      </div>
      
      {loading ? (
        <div className="p-8 text-center">
          <Loader2 className="w-6 h-6 text-soft-amber animate-spin mx-auto" />
        </div>
      ) : activity.length === 0 ? (
        <div className="p-8 text-center text-muted-text">No recent activity</div>
      ) : (
        <div className="divide-y divide-white/[0.06] max-h-[400px] overflow-y-auto">
          {activity.map((item, index) => {
            const Icon = getIcon(item.type);
            const colorClass = getColor(item.type);
            return (
              <div key={index} className="flex items-start gap-3 p-4 hover:bg-white/[0.02]">
                <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", colorClass)}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-cloud-white text-sm truncate">{item.title}</p>
                  <p className="text-xs text-muted-text">{item.description}</p>
                </div>
                <span className="text-xs text-muted-text font-mono shrink-0">
                  {formatDate(item.timestamp)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ==================== REVENUE CARDS ====================
const RevenueCards = ({ revenue, loading }) => {
  const items = [
    { label: 'Subscription Revenue', value: revenue?.subscription_revenue || 0, icon: CreditCard, color: 'electric-ice-blue' },
    { label: 'Ticket Revenue', value: revenue?.ticket_revenue || 0, icon: Ticket, color: 'cool-mint' },
    { label: 'Sponsor Revenue', value: revenue?.sponsor_revenue || 0, icon: Megaphone, color: 'soft-amber' },
  ];

  return (
    <div className="bg-graphite/50 rounded-xl border border-white/[0.06] overflow-hidden" data-testid="revenue-cards">
      <div className="p-4 border-b border-white/[0.06]">
        <h3 className="font-semibold text-cloud-white flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-cool-mint" />
          Revenue Overview
        </h3>
      </div>
      
      <div className="grid grid-cols-3 divide-x divide-white/[0.06]">
        {items.map((item, index) => {
          const Icon = item.icon;
          return (
            <div key={index} className="p-4 text-center">
              <div className={cn(
                "w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2",
                `bg-${item.color}/10`
              )}>
                <Icon className={cn("w-5 h-5", `text-${item.color}`)} />
              </div>
              <p className="text-xl font-bold text-cloud-white font-mono">
                ${formatNumber(item.value)}
              </p>
              <p className="text-xs text-muted-text mt-1">{item.label}</p>
              {item.value === 0 && (
                <BadgePill variant="default" size="sm" className="mt-2">Coming Soon</BadgePill>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==================== SUPPORT ALERTS ====================
const SupportAlerts = ({ alerts, loading }) => {
  return (
    <div className="bg-graphite/50 rounded-xl border border-white/[0.06] overflow-hidden" data-testid="support-alerts">
      <div className="p-4 border-b border-white/[0.06]">
        <h3 className="font-semibold text-cloud-white flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-signal-red" />
          Support Alerts
        </h3>
      </div>
      
      {loading ? (
        <div className="p-8 text-center">
          <Loader2 className="w-6 h-6 text-signal-red animate-spin mx-auto" />
        </div>
      ) : alerts.length === 0 ? (
        <div className="p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-cool-mint/10 flex items-center justify-center mx-auto mb-3">
            <Users className="w-6 h-6 text-cool-mint" />
          </div>
          <p className="text-soft-silver text-sm">No active alerts</p>
          <p className="text-xs text-muted-text mt-1">All systems operating normally</p>
        </div>
      ) : (
        <div className="divide-y divide-white/[0.06]">
          {alerts.map((alert, index) => (
            <div key={index} className="flex items-center gap-3 p-4 bg-signal-red/5">
              <AlertTriangle className="w-5 h-5 text-signal-red shrink-0" />
              <div className="flex-1">
                <p className="font-medium text-cloud-white text-sm">{alert.title}</p>
                <p className="text-xs text-muted-text">{alert.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ==================== MAIN PLATFORM DASHBOARD ====================
export const Dashboard = () => {
  const { session } = useAuth();

  // State
  const [stats, setStats] = useState(null);
  const [campuses, setCampuses] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [activity, setActivity] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Fetch all data
  const fetchData = useCallback(async (isRefresh = false) => {
    if (!session?.access_token) {
      setLoading(false);
      return;
    }

    if (isRefresh) setRefreshing(true);

    try {
      const headers = { Authorization: `Bearer ${session.access_token}` };

      // Fetch all data in parallel
      const [statsRes, campusesRes, orgsRes, activityRes, revenueRes, alertsRes] = await Promise.all([
        fetch(`${API_URL}/api/platform/stats`, { headers }),
        fetch(`${API_URL}/api/platform/campuses?page_size=5`, { headers }),
        fetch(`${API_URL}/api/platform/organizations?page_size=5`, { headers }),
        fetch(`${API_URL}/api/platform/activity?limit=10`, { headers }),
        fetch(`${API_URL}/api/platform/revenue`, { headers }),
        fetch(`${API_URL}/api/platform/support/alerts`, { headers }),
      ]);

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      if (campusesRes.ok) {
        const data = await campusesRes.json();
        setCampuses(data.campuses || []);
      }

      if (orgsRes.ok) {
        const data = await orgsRes.json();
        setOrganizations(data.organizations || []);
      }

      if (activityRes.ok) {
        const data = await activityRes.json();
        setActivity(data.activity || []);
      }

      if (revenueRes.ok) {
        const data = await revenueRes.json();
        setRevenue(data);
      }

      if (alertsRes.ok) {
        const data = await alertsRes.json();
        setAlerts(data.alerts || []);
      }
    } catch (error) {
      console.error('Failed to fetch platform data:', error);
      toast.error('Failed to load platform data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [session?.access_token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-electric-ice-blue animate-spin" />
          <p className="text-soft-silver text-sm">Loading platform dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="platform-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-cloud-white">Platform Dashboard</h1>
          <p className="text-soft-silver text-sm mt-1">CamPassed platform overview and analytics</p>
        </div>
        <Button
          variant="outline"
          onClick={() => fetchData(true)}
          disabled={refreshing}
          className="border-white/[0.08] text-soft-silver hover:text-cloud-white"
        >
          <RefreshCw className={cn("w-4 h-4 mr-2", refreshing && "animate-spin")} />
          Refresh
        </Button>
      </div>

      {/* Stat Strip */}
      <StatStrip stats={stats} loading={loading} />

      {/* Main Grid */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Campuses */}
        <TopCampusesTable campuses={campuses} loading={loading} />

        {/* Top Organizations */}
        <TopOrganizationsTable organizations={organizations} loading={loading} />
      </div>

      {/* Second Row */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Activity Feed */}
        <div className="lg:col-span-2">
          <ActivityFeed activity={activity} loading={loading} />
        </div>

        {/* Support Alerts */}
        <SupportAlerts alerts={alerts} loading={loading} />
      </div>

      {/* Revenue Section */}
      <RevenueCards revenue={revenue} loading={loading} />
    </div>
  );
};

export default Dashboard;
